import React, { useMemo } from 'react';
import { Table } from 'tea-component';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import useIntls from '@opm/hooks/useIntls';
import { DBO_V } from '@op/utils/format';
import { DefaultImg } from '@op/constants/defaultImg';
import type { TGameMap } from '@opm/pages/CompetitorAdComparison/hooks/useGameMap';
import { ITrendItemBase, ITopCreative, IListMax, trendKey } from '../types';
import { getAiSummaryText, TTopicTypeColorMap } from '../utils';
import TrendTopicCell from './components/TrendTopicCell';
import MetricsCell from './components/MetricsCell';
import PreviewThumbnails from '../components/PreviewThumbnails';
import './index.scss';
import CardCommon from '@op/components/CardCommon';
import TextBubbles from '@home/components/TextBubbles';
import AdsIcon from '@opm/assets/svg/icon-ads.svg';

const { injectable } = Table.addons;

export interface IMetricColumnConfig {
  key: string;
  dataKey: string;
  maxKey?: string;
  headerTextKey: string;
  width?: number;
  align?: 'left' | 'right' | 'center';
  decimal?: number;
  withHeaderIcon?: boolean;
  sortKey?: string;
}

export const DEFAULT_METRIC_COLUMNS: IMetricColumnConfig[] = [
  {
    key: 'impressions',
    dataKey: 'cumulative_impressions',
    maxKey: 'cumulative_impressions',
    headerTextKey: 'Impressions',
    width: 240,
    align: 'right',
    withHeaderIcon: true,
    sortKey: 'impressions',
  },
  {
    key: 'creatives',
    dataKey: 'creatives',
    maxKey: 'creatives',
    headerTextKey: 'Ads-1',
    width: 240,
    align: 'right',
    sortKey: 'creatives',
  },
  {
    key: 'avg_days_active',
    dataKey: 'avg_days_active',
    maxKey: 'avg_days_active',
    headerTextKey: 'Avg. Days Active',
    width: 240,
    align: 'right',
    decimal: 1,
    sortKey: 'avg_days_active',
  },
];

export interface IListViewProps {
  data: ITrendItemBase[];
  total: number;
  loading: boolean;
  /** 当前 tab + filter 下全量 KPI 最大值（list.md `max` 字段，进度条归一化用） */
  listMax?: IListMax;
  /** 游戏元信息字典（cond.materials_config_games）：id → meta */
  gameMap?: TGameMap;
  /** Topic Type chip 颜色映射（按 L1 parent 取色，L2 固定灰色） */
  topicTypeColorMap?: TTopicTypeColorMap;
  pageInfo: { page: number; page_size: number };
  changePageInfo: (info?: { page: number; page_size: number }) => void;
  sortBy: SortBy<string>;
  setSortBy: (value: SortBy<string>) => void;
  /** 当前 filter（用于 DoD/WoW/MoM 文案 + currentEndTime fallback） */
  filter?: any;
  onRowClick?: (record: ITrendItemBase) => void;
  onTitleClick?: (record: ITrendItemBase) => void;
  onThumbnailClick?: (item: ITopCreative, record: ITrendItemBase) => void;
  /** 选中项 key，由 trendKey(record) 生成 */
  selectedTrendKey?: string;
  metricColumns?: IMetricColumnConfig[];
  /**
   * 缩略图列取值器（默认 `record.top_related_creatives`）。
   * UGC 等扩展页可注入 `(r) => r.top_related_videos`。
   */
  thumbnailsAccessor?: (record: ITrendItemBase) => any[] | undefined;
  shareIcon?: boolean;
}

/**
 * ListView 表格视图（7 列）
 *
 * 列定义：
 * 1. # 排名
 * 2. Preview 缩略图（叠加 3 张）
 * 3. Trend Topic（标题 + 标签）
 * 4. Impressions（数值 + 环比 + 进度条）
 * 5. Creatives（数值 + 环比）
 * 6. Avg.Days Active（数值）
 * 7. AI Insight（渐变文字）
 */
export default function ListView({
  data,
  total,
  loading,
  listMax,
  gameMap,
  topicTypeColorMap,
  pageInfo,
  changePageInfo,
  sortBy,
  setSortBy,
  filter,
  onRowClick,
  onTitleClick,
  onThumbnailClick,
  selectedTrendKey,
  metricColumns = DEFAULT_METRIC_COLUMNS,
  thumbnailsAccessor,
  shareIcon = false,
}: IListViewProps) {
  const { intl, isEn } = useIntls();

  const columns = useMemo(() => {
    const metricColumnDefs = metricColumns.map((metricConfig) => ({
      key: metricConfig.key,
      header: metricConfig.withHeaderIcon ? (
        <div className="dbom-ct-table-header-with-sort">
          <img src={AdsIcon} className="dbom-table-header-metrics-icon" />
          {intl(metricConfig.headerTextKey)}
        </div>
      ) : (
        intl(metricConfig.headerTextKey)
      ),
      width: metricConfig.width || 220,
      align: metricConfig.align || 'right',
      render: (record: ITrendItemBase) => (
        <MetricsCell
          kpiData={(record as any)?.[metricConfig.dataKey]}
          max={(listMax as any)?.[metricConfig.maxKey || metricConfig.dataKey]}
          formatValue={
            metricConfig.decimal !== undefined
              ? (val) => DBO_V.reviews(val, metricConfig.decimal)
              : DBO_V.reviews
          }
        />
      ),
    }));

    return [
      {
        key: 'preview',
        header: intl('Preview'),
        width: 160,
        render: (record: ITrendItemBase) => (
          <PreviewThumbnails
            list={
              thumbnailsAccessor
                ? thumbnailsAccessor(record)
                : (record as any).top_related_creatives
            }
            maxCount={3}
            onClick={(item) => onThumbnailClick?.(item, record)}
            shareIcon={shareIcon}
          />
        ),
      },
      {
        key: 'trend_topic',
        header: intl('Trend Topic'),
        width: 330,
        fixed: 'left',
        render: (record: ITrendItemBase) => (
          <TrendTopicCell
            record={record}
            gameMap={gameMap}
            topicTypeColorMap={topicTypeColorMap}
            onTitleClick={onTitleClick}
          />
        ),
      },
      ...metricColumnDefs,
      {
        key: 'ai_summary',
        header: (
          <div className="dbom-ct-table-ai-header">
            <img
              src={DefaultImg.AI}
              className="dbom-ct-table-ai-header__icon"
            />
            <span className="dbom-ct-table-ai-header__text">
              {intl('AI Summary')}
            </span>
          </div>
        ),
        render: (record: ITrendItem) => (
          <TextBubbles
            text={getAiSummaryText(record, isEn)}
            rowNum={3}
            className="dbom-ct-table-ai-text"
          />
        ),
      },
    ];
  }, [
    intl,
    pageInfo,
    sortBy,
    setSortBy,
    listMax,
    gameMap,
    filter,
    onTitleClick,
    onThumbnailClick,
    metricColumns,
    thumbnailsAccessor,
  ]);

  return (
    <CardCommon className="dbom-ct-list-view">
      <TableCommonHook
        className="dbom-ct-list-view-table"
        sortInfo={[sortBy, setSortBy]}
        records={data}
        total={total}
        loading={loading}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        sortColumns={metricColumns.map((item) => item.sortKey || item.key)}
        recordKey={(item: ITrendItemBase) => trendKey(item)}
        columns={columns as any}
        rowClassName={(record: ITrendItemBase) =>
          selectedTrendKey && trendKey(record) === selectedTrendKey
            ? 'dbom-selected-row'
            : ''
        }
        addons={[
          injectable({
            row: (_props: any, context: any) => ({
              onClick: () => onRowClick?.(context.record),
            }),
          }),
        ]}
        minWidth={1600}
      />
    </CardCommon>
  );
}
